/**
 * ComponentPalette - Flow component palette
 *
 * This component uses the FlowComponentRegistry to dynamically render available components
 * organized by category. No hardcoded component definitions.
 */

import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Box, Typography, Paper, TextField, Collapse, InputAdornment, Tooltip } from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { flowComponentRegistry } from './flow-components';

const DraggableComponent = ({ flowComponent }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `palette-${flowComponent.type}`,
    data: {
      type: 'component',
      componentType: flowComponent.type,
      defaultProperties: flowComponent.getDefaultProperties(),
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Tooltip
      title={flowComponent.description || ''}
      placement="right"
      enterDelay={400}
    >
      <Paper
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className="component-item"
        data-testid={`palette-item-${flowComponent.type}`}
        style={style}
        elevation={1}
      >
        <Box className="component-item__icon">{flowComponent.icon}</Box>
        <Box className="component-item__content">
          <Typography variant="body2" className="component-item__name">
            {flowComponent.name}
          </Typography>
          {flowComponent.maxPerScreen && (
            <Typography variant="caption" className="component-item__limit">
              Max: {flowComponent.maxPerScreen}
            </Typography>
          )}
        </Box>
      </Paper>
    </Tooltip>
  );
};

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const ComponentPalette = () => {
  // Get all categories from the registry
  const categories = flowComponentRegistry.getCategories();
  const [query, setQuery] = useState('');
  // category -> true when collapsed (expanded by default)
  const [collapsed, setCollapsed] = useState({});

  const q = query.trim().toLowerCase();
  const isSearching = q.length > 0;

  // Match user-facing name and description only, users shouldn't need to know
  // internal type codes (e.g. "TextInput") to find a component.
  const matches = (c) =>
    c.name.toLowerCase().includes(q) ||
    (c.description || '').toLowerCase().includes(q);

  const toggleCategory = (category) =>
    setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));

  const searchResults = isSearching
    ? categories
        .flatMap((category) => flowComponentRegistry.getByCategory(category))
        .filter(matches)
    : [];

  return (
    <Box className="component-palette" data-testid="component-palette">
      <Typography variant="h6" className="component-palette__title">
        Components
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search components"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="palette-search"
        inputProps={{ 'aria-label': 'Search components' }}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {isSearching ? (
        searchResults.length > 0 ? (
          <Box className="component-group__items">
            {searchResults.map((flowComponent) => (
              <DraggableComponent key={flowComponent.type} flowComponent={flowComponent} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            No components match &ldquo;{query}&rdquo;.
          </Typography>
        )
      ) : (
        categories.map((category) => {
          const flowComponents = flowComponentRegistry.getByCategory(category);
          const isCollapsed = !!collapsed[category];

          return (
            <Box key={category} className="component-group">
              <Box
                className="component-group__header"
                onClick={() => toggleCategory(category)}
                role="button"
                aria-expanded={!isCollapsed}
              >
                <Typography variant="subtitle2" className="component-group__title">
                  {capitalize(category)}
                </Typography>
                {isCollapsed ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ExpandLessIcon fontSize="small" />
                )}
              </Box>

              <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                <Box className="component-group__items">
                  {flowComponents.map((flowComponent) => (
                    <DraggableComponent key={flowComponent.type} flowComponent={flowComponent} />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default ComponentPalette;
